'use client';

import { type ReactNode, createContext, useContext } from 'react';

import { useIsMobile } from '@kit/ui/use-mobile';

import type { PublicLocationRow } from '~/(tenant)/book/_lib/server/booking-page.loader';

import { type VenueOption, useVenuePicker } from './use-venue-picker';
import { VenuePickerSurface } from './venue-picker-surface';
import { shouldPromptWebFirstPick } from './venue-picker-routes';

interface WebVenuePickerContextValue {
  activeVenue: VenueOption | null;
  open: () => void;
}

const WebVenuePickerContext = createContext<WebVenuePickerContextValue | null>(
  null,
);

/** Null outside the web layout or for single-venue tenants: nothing to pick. */
export function useWebVenuePicker() {
  return useContext(WebVenuePickerContext);
}

/**
 * Web counterpart of the native top-bar venue chip, mounted by the layout
 * shell whenever the native shell is NOT showing (desktop, and mobile web for
 * tenants without the app experience). First-time visitors on a catalog page
 * get the picker auto-opened; afterwards the page-level VenueBar re-opens it.
 *
 * The trigger lives in page content rather than in the header because several
 * tenants ship bespoke, fixed-position headers that would have to be wired
 * one by one.
 */
export function WebVenuePicker({
  accountId,
  locations,
  funnelLocations,
  children,
}: {
  accountId: string;
  locations: VenueOption[];
  funnelLocations?: PublicLocationRow[];
  children: ReactNode;
}) {
  const isMobile = useIsMobile();
  const picker = useVenuePicker({
    accountId,
    venues: locations,
    shouldPromptFirstPick: shouldPromptWebFirstPick,
    // Until the viewport is known, the layout shell may still swap this tree
    // for the native one (mobile web with the app experience), so hold the
    // prompt rather than flash a dialog that is about to unmount.
    ready: isMobile !== undefined,
  });
  const hasChoice = picker.venues.length >= 2;

  return (
    <WebVenuePickerContext.Provider
      value={
        hasChoice ? { activeVenue: picker.activeVenue, open: picker.open } : null
      }
    >
      {children}
      {hasChoice && isMobile !== undefined ? (
        <VenuePickerSurface
          picker={picker}
          variant={isMobile ? 'sheet' : 'dialog'}
          funnelLocations={funnelLocations}
        />
      ) : null}
    </WebVenuePickerContext.Provider>
  );
}
